import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Plus, Search } from 'lucide-react';
import { DataTable, type Column } from '../components/data-table';
import { FormFooter, PageHeader, Row, Section, Select } from '../components/form';
import { Alert, Badge, Button, Input, cn } from '../components/ui';
import { Checkbox } from '../components/ui/checkbox';
import { ApiError, api, type Extension } from '../lib/api';

interface Member {
  extensionId: string;
  userType: string;
  customPermissions: string[];
}

interface Group {
  id: string;
  name: string;
  isAllExtensions: boolean;
  managerPermissions: string[];
  userPermissions: string[];
  members: Member[];
}

interface Permission {
  key: string;
  label: string;
}

const USER_TYPES = [
  { value: 'manager', label: 'Manager' },
  { value: 'user', label: 'User' },
  { value: 'custom', label: 'Custom' },
] as const;

export function ExtensionGroups() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);

  const { data: groups = [] } = useQuery({
    queryKey: ['extension-groups'],
    queryFn: () => api.get<Group[]>('/api/v1/extension-groups'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/extension-groups/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['extension-groups'] }),
  });

  if (editing !== null) {
    return (
      <GroupEditor
        id={editing === 'new' ? null : editing}
        onClose={() => {
          setEditing(null);
          void queryClient.invalidateQueries({ queryKey: ['extension-groups'] });
        }}
      />
    );
  }

  const columns: Array<Column<Group>> = [
    {
      key: 'name',
      header: 'Name',
      sort: (a, b) => a.name.localeCompare(b.name),
      render: (g) => (
        <span className="flex items-center gap-2">
          <span className="font-medium">{g.name}</span>
          {g.isAllExtensions && <Badge>Built in</Badge>}
        </span>
      ),
    },
    {
      key: 'members',
      header: 'Members',
      sort: (a, b) => a.members.length - b.members.length,
      render: (g) => <span className="tabular-nums">{g.members.length}</span>,
    },
    {
      key: 'managers',
      header: 'Managers',
      render: (g) => {
        const managers = g.members.filter((m) => m.userType === 'manager').length;
        return managers > 0 ? (
          <span className="tabular-nums">{managers}</span>
        ) : (
          <span className="text-muted-foreground">None</span>
        );
      },
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Extension groups"
        description="Who may see, transfer, pick up and monitor whose calls."
        actions={
          <Button onClick={() => setEditing('new')}>
            <Plus size={14} />
            New group
          </Button>
        }
      />

      <Alert tone="warning">
        Permissions are scoped to a group, not the whole PBX. A manager may act on the calls of
        people they share a group with, and nobody else's.
      </Alert>

      <DataTable
        rows={groups}
        columns={columns}
        rowKey={(g) => g.id}
        search={(g) => g.name}
        searchPlaceholder="Search groups"
        onEdit={(g) => setEditing(g.id)}
        onDelete={(rows) => rows.forEach((r) => remove.mutate(r.id))}
        deleteLabel={() => 'Delete this group? Its permissions stop applying immediately.'}
        empty="No groups yet."
      />
    </div>
  );
}

function GroupEditor({ id, onClose }: { id: string | null; onClose: () => void }) {
  const [loaded, setLoaded] = useState(false);
  const [name, setName] = useState('');
  const [members, setMembers] = useState<Member[]>([]);
  const [managerPermissions, setManagerPermissions] = useState<string[]>([]);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [availableQuery, setAvailableQuery] = useState('');
  const [selectedQuery, setSelectedQuery] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const { data: permissions = [] } = useQuery({
    queryKey: ['group-permissions'],
    queryFn: () => api.get<Permission[]>('/api/v1/extension-groups/permissions'),
  });

  const { data: extensions = [] } = useQuery({
    queryKey: ['extensions'],
    queryFn: () => api.get<Extension[]>('/api/v1/extensions'),
  });

  const existing = useQuery({
    queryKey: ['extension-group', id],
    queryFn: () => api.get<Group>(`/api/v1/extension-groups/${id}`),
    enabled: id !== null,
  });

  if (id !== null && existing.data && !loaded) {
    setName(existing.data.name);
    setMembers(existing.data.members);
    setManagerPermissions(existing.data.managerPermissions);
    setUserPermissions(existing.data.userPermissions);
    setLoaded(true);
  }

  // Defaults for a new group come from the server's list rather than a copy kept here, so they
  // cannot drift from what the API would apply.
  if (id === null && !loaded && permissions.length > 0) {
    setManagerPermissions([
      'switch_presence',
      'call_distribution',
      'pickup_hangup',
      'monitoring',
      'parking',
      'call_flow',
    ]);
    setLoaded(true);
  }

  const save = useMutation({
    mutationFn: () => {
      const body = { name, managerPermissions, userPermissions, members };
      return id === null
        ? api.post('/api/v1/extension-groups', body)
        : api.patch(`/api/v1/extension-groups/${id}`, body);
    },
    onSuccess: onClose,
  });

  const memberIds = new Set(members.map((m) => m.extensionId));
  const available = extensions.filter(
    (e) =>
      !memberIds.has(e.id) &&
      `${e.number} ${e.name}`.toLowerCase().includes(availableQuery.toLowerCase()),
  );
  const selected = members.filter((m) => {
    const ext = extensions.find((e) => e.id === m.extensionId);
    return `${ext?.number ?? ''} ${ext?.name ?? ''}`
      .toLowerCase()
      .includes(selectedQuery.toLowerCase());
  });

  const togglePicked = (extensionId: string) => {
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(extensionId)) next.delete(extensionId);
      else next.add(extensionId);
      return next;
    });
  };

  const add = () => {
    const toAdd = [...picked].filter((extensionId) => !memberIds.has(extensionId));
    setMembers((current) => [
      ...current,
      // New members default to plain user, which grants nothing — adding somebody to a group
      // should never be the act that hands them control of a colleague's calls.
      ...toAdd.map((extensionId) => ({ extensionId, userType: 'user', customPermissions: [] })),
    ]);
    setPicked(new Set());
  };

  const removePicked = () => {
    setMembers((current) => current.filter((m) => !picked.has(m.extensionId)));
    setPicked(new Set());
  };

  const label = (extensionId: string) => {
    const ext = extensions.find((e) => e.id === extensionId);
    return ext ? `${ext.number} — ${ext.name}` : extensionId;
  };

  const conflict = save.error instanceof ApiError && save.error.isConflict;

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <PageHeader title={id === null ? 'New extension group' : `Edit ${name}`} />

      {save.error && (
        <Alert>
          {conflict ? 'A group with that name already exists.' : (save.error as Error).message}
        </Alert>
      )}

      <Section title="Basic">
        <div className="max-w-md">
          <Row label="Name" required>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Sales team"
              required
            />
          </Row>
        </div>
      </Section>

      <Section
        title="Members"
        description="Pick extensions on the left, then give each one a user type on the right."
      >
        <div className="grid gap-3 lg:grid-cols-[1fr_auto_1.4fr]">
          <div className="rounded-lg border">
            <div className="border-b p-2">
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  value={availableQuery}
                  onChange={(e) => setAvailableQuery(e.target.value)}
                  placeholder={`Available (${available.length})`}
                  className="pl-8"
                  aria-label="Search available extensions"
                />
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto p-1">
              {available.length === 0 ? (
                <p className="p-3 text-sm text-muted-foreground">Everyone is already a member.</p>
              ) : (
                available.map((ext) => (
                  <label
                    key={ext.id}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted"
                  >
                    <Checkbox
                      checked={picked.has(ext.id)}
                      onCheckedChange={() => togglePicked(ext.id)}
                    />
                    <span className="font-mono">{ext.number}</span>
                    <span className="truncate text-muted-foreground">{ext.name}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          <div className="flex flex-row items-center justify-center gap-2 lg:flex-col">
            <Button type="button" variant="secondary" onClick={add} aria-label="Add selected">
              <ChevronRight size={15} />
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={removePicked}
              aria-label="Remove selected"
            >
              <ChevronLeft size={15} />
            </Button>
          </div>

          <div className="rounded-lg border">
            <div className="border-b p-2">
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  value={selectedQuery}
                  onChange={(e) => setSelectedQuery(e.target.value)}
                  placeholder={`Members (${members.length})`}
                  className="pl-8"
                  aria-label="Search members"
                />
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto p-1">
              {selected.length === 0 ? (
                <p className="p-3 text-sm text-muted-foreground">No members yet.</p>
              ) : (
                selected.map((member) => (
                  <div
                    key={member.extensionId}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted"
                  >
                    <Checkbox
                      checked={picked.has(member.extensionId)}
                      onCheckedChange={() => togglePicked(member.extensionId)}
                    />
                    <span className="min-w-0 flex-1 truncate">{label(member.extensionId)}</span>
                    <div className="w-32">
                      <Select
                        value={member.userType}
                        onChange={(userType) =>
                          setMembers((current) =>
                            current.map((m) =>
                              m.extensionId === member.extensionId ? { ...m, userType } : m,
                            ),
                          )
                        }
                        aria-label={`User type for ${label(member.extensionId)}`}
                      >
                        {USER_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </Select>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {members.some((m) => m.userType === 'custom') && (
          <div className="mt-4 space-y-3">
            <p className="text-sm font-medium">Custom permissions</p>
            {members
              .filter((m) => m.userType === 'custom')
              .map((member) => (
                <div key={member.extensionId} className="rounded-lg border p-3">
                  <p className="mb-2 text-sm font-medium">{label(member.extensionId)}</p>
                  <PermissionGrid
                    permissions={permissions}
                    granted={member.customPermissions}
                    onChange={(next) =>
                      setMembers((current) =>
                        current.map((m) =>
                          m.extensionId === member.extensionId
                            ? { ...m, customPermissions: next }
                            : m,
                        ),
                      )
                    }
                  />
                </div>
              ))}
          </div>
        )}
      </Section>

      <Section
        title="What each user type may do"
        description="Applies only to the calls of other members of this group."
      >
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <p className="mb-2 text-sm font-medium">Manager</p>
            <PermissionGrid
              permissions={permissions}
              granted={managerPermissions}
              onChange={setManagerPermissions}
            />
          </div>
          <div>
            <p className="mb-2 text-sm font-medium">User</p>
            <PermissionGrid
              permissions={permissions}
              granted={userPermissions}
              onChange={setUserPermissions}
            />
          </div>
        </div>
      </Section>

      <FormFooter onCancel={onClose} saving={save.isPending} disabled={!name} />
    </form>
  );
}

function PermissionGrid({
  permissions,
  granted,
  onChange,
}: {
  permissions: Permission[];
  granted: string[];
  onChange: (next: string[]) => void;
}) {
  return (
    <div className="space-y-1.5">
      {permissions.map((permission) => (
        <label
          key={permission.key}
          className={cn(
            'flex cursor-pointer items-center gap-2 rounded-md px-1 py-1 text-sm',
            'hover:bg-muted',
          )}
        >
          <Checkbox
            checked={granted.includes(permission.key)}
            onCheckedChange={(checked) =>
              onChange(
                checked === true
                  ? [...granted, permission.key]
                  : granted.filter((p) => p !== permission.key),
              )
            }
          />
          {permission.label}
        </label>
      ))}
    </div>
  );
}
